import * as React from 'react'

import { Button } from '@/components/ui/button'

import { PdfViewerBottomControls } from './pdf-viewer-bottom-controls'
import { PdfViewerCanvas } from './pdf-viewer-canvas'
import { PdfViewerHeader } from './pdf-viewer-header'
import { PdfViewerProvider, usePdfViewerContext } from './pdf-viewer-provider'
import { PdfViewerToc } from './pdf-viewer-toc'
import { PdfViewerToolbar } from './pdf-viewer-toolbar'

interface PdfViewerProps {
  sourceUrl: string
  title?: string
  onLoadingComplete?: () => void
  isDownloadable?: boolean
}

function PdfViewerContent({
  title,
  sourceUrl,
  isDownloadable = false,
}: {
  title?: string
  sourceUrl: string
  isDownloadable?: boolean
}) {
  const { containerRef, error, loading, retry } = usePdfViewerContext()

  if (loading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-zinc-200">
        <div className="text-center">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
          <p className="mt-3 text-sm text-zinc-700">Loading PDF...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-zinc-200 p-6">
        <div className="text-center">
          <p className="font-semibold text-destructive">Unable to display this PDF</p>
          <p className="mt-1 text-sm text-zinc-600">{error}</p>
          <Button className="mt-4" onClick={retry} variant="outline">
            Retry
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div ref={containerRef} className="relative flex flex-col w-full overflow-hidden bg-zinc-200">
      <PdfViewerHeader title={title} sourceUrl={sourceUrl} isDownloadable={isDownloadable} />
      <div className="h-[calc(100vh-3.5rem)] w-full">
        <PdfViewerToolbar />
        <PdfViewerCanvas />
        <PdfViewerBottomControls />
        <PdfViewerToc />
      </div>
    </div>
  )
}

export function PdfViewer({ onLoadingComplete, sourceUrl, title, isDownloadable = false }: PdfViewerProps) {
  return (
    <PdfViewerProvider onLoadingComplete={onLoadingComplete} sourceUrl={sourceUrl}>
      <PdfViewerContent title={title} sourceUrl={sourceUrl} isDownloadable={isDownloadable} />
    </PdfViewerProvider>
  )
}
